import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classnames from 'classnames'
import { Field, type FormikValues } from 'formik'
import { t as tt } from 'i18next'
import * as React from 'react'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { AssignmentUp } from 'src/assets/icons/customIcons/AssignmentUp'
import { XlsIcon } from 'src/assets/icons/customIcons/Xls'
import { errorMessage } from 'src/helpers/fns'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import * as Table from 'src/tailwind/components/Table'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import Swal from 'sweetalert2'
import {
  CHOICE_INPUT_CLASS_NAME,
  CHOICE_STATUS_PASSED,
  CREDIT_TYPE_PRE_ARGUS,
  CREDIT_TYPE_RECOGNIZED,
  FREE_CREDITS_PROGRAM_ID,
  GENERAL_MODULE_PROGRAM_ID,
  useStudentChoiceParams,
} from './constants'
import { type ScoreFormatter, useChoiceScoreFormatter } from './useChoiceScoreFormatter'
import { useOpenExportFile } from './useOpenExportFile'

const COLUMN_COUNT = 9

/** Legacy always lists the general module first, then the major, then the minor or free credits. */
const BUCKET_ORDER: readonly Api.AdministratorChoiceBucket[] = ['general', 'major', 'minor', 'free', 'other', 'passive']

function courseNameOf(choice: Api.AdministratorChoiceHistory3Row, locale: string): string {
  return (locale === 'en' && choice.courseNameEn !== '' ? choice.courseNameEn : choice.courseName).trim()
}

/** A program a row can be moved into; `name` is shown in the tooltip. */
interface AssignmentTarget {
  readonly programId: number
  readonly name: string
}

/** The student's major or minor program; `code` decides which rows belong to it. */
interface ProgramTarget extends AssignmentTarget {
  readonly code: string
}

/** The programs a row can be moved between; a student with a minor has no free credits to move rows into. */
interface ProgramTargets {
  readonly major: ProgramTarget | null
  readonly minor: ProgramTarget | null
}

function programTargetOf(program: Api.AdministratorChoiceStudentProgram | null | undefined): ProgramTarget | null {
  return program != null ? { programId: program.id, name: program.name, code: program.code } : null
}

export default function AdministratorStudentChoicesSortedCoursesPage(): JSX.Element {
  const t = useTranslatable()
  return (
    <SuspenseWrapper title={t('choice:sorted_courses')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const canManageUsers = useAdminAccess().canManage('userStudents')
  const headers = useAuthenticatedHeaders()
  const { uid, profileId } = useStudentChoiceParams()
  const formatScore = useChoiceScoreFormatter()

  const [editingKey, setEditingKey] = React.useState<string | null>(null)
  const [expandCodes, setExpandCodes] = React.useState(false)

  const {
    data: historyPage,
    mutate,
    isLoading,
  } = useApi({
    endpoint: Api.getAdministratorStudentChoiceHistory3,
    params: React.useMemo(() => ({ headers, args: { uid, profileId } }), [headers, uid, profileId]),
    shouldFetch: uid !== '' && profileId !== '',
    suspense: false,
  })

  // Old Argus falls back to the preferred major when the student has no current program.
  const majorProgram = historyPage?.student.program ?? historyPage?.student.preferredMajorProgram
  const minorProgram = historyPage?.student.preferredMinorProgram
  const programTargets = React.useMemo<ProgramTargets>(
    () => ({ major: programTargetOf(majorProgram), minor: programTargetOf(minorProgram) }),
    [majorProgram, minorProgram]
  )

  const { isExporting, openExportFile } = useOpenExportFile()
  const openHistoryExport = React.useCallback(
    (mode: 'print' | 'download') =>
      void openExportFile(
        async () =>
          await Api.getAdministratorStudentChoiceHistory3Export({ headers, args: { uid, profileId }, query: { mode } })
      ),
    [headers, openExportFile, profileId, uid]
  )
  const handleExport = React.useCallback(() => openHistoryExport('download'), [openHistoryExport])
  const handlePrint = React.useCallback(() => openHistoryExport('print'), [openHistoryExport])

  const stopEditing = React.useCallback(() => setEditingKey(null), [])
  const toggleExpandCodes = React.useCallback(() => setExpandCodes((expanded) => !expanded), [])

  const handleReset = React.useCallback(async () => {
    const alert = await Swal.fire({
      title: t('common:are_you_sure'),
      text: t('choice:reset_assignments_confirm'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('common:confirm'),
      cancelButtonText: t('common:cancel'),
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6C757D',
    })
    if (!alert.isConfirmed) return
    try {
      await Api.postAdministratorStudentChoiceProgramAssignmentsReset({ headers, args: { uid, profileId } })
      await mutate()
      toast.success(t('common:updated_successfully'))
    } catch (error) {
      toast.error(errorMessage(error as base.CommonError))
    }
  }, [headers, mutate, profileId, t, uid])

  const handleAssign = React.useCallback(
    async (choice: Api.AdministratorChoiceHistory3Row, programId: number) => {
      try {
        await Api.patchAdministratorStudentChoiceProgramAssignment({
          headers,
          args: { uid, profileId, choiceUid: choice.uid },
          body: { targetSemesterId: choice.semId, programId },
        })
        await mutate()
        toast.success(t('common:updated_successfully'))
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
      }
    },
    [headers, mutate, profileId, t, uid]
  )

  const handleUpdate = React.useCallback(
    async (choice: Api.AdministratorChoiceHistory3Row, values: FormikValues) => {
      try {
        await Api.patchAdministratorStudentChoice({
          headers,
          args: { uid, profileId, choiceUid: choice.uid },
          body: {
            targetSemesterId: choice.semId,
            couName: values.couName as string,
            couCode: values.couCode as string,
            couGeneral: (values.couGeneral as boolean) ? 1 : 0,
          },
        })
        setEditingKey(null)
        await mutate()
        toast.success(t('common:updated_successfully'))
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
        // Rethrow so <FormError /> renders the 422 field errors instead of only a toast.
        throw error
      }
    },
    [headers, mutate, profileId, t, uid]
  )

  const handleToggleDiscard = React.useCallback(
    async (choice: Api.AdministratorChoiceHistory3Row) => {
      const alert = await Swal.fire({
        title: t('common:are_you_sure'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('common:confirm'),
        cancelButtonText: t('common:cancel'),
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6C757D',
      })
      if (!alert.isConfirmed) return
      const params = {
        headers,
        args: { uid, profileId, choiceUid: choice.uid },
        query: { targetSemesterId: choice.semId },
      }
      try {
        if (choice.isDiscarded) {
          await Api.patchAdministratorStudentChoiceUndiscard(params)
        } else {
          await Api.patchAdministratorStudentChoiceDiscard(params)
        }
        await mutate()
        toast.success(t('common:updated_successfully'))
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
      }
    },
    [headers, mutate, profileId, t, uid]
  )

  if (isLoading) return <Loader className="m-auto mt-8 flex" />

  const groups = (historyPage?.groups ?? [])
    .filter((group) => group.items.length > 0)
    .sort((a, b) => BUCKET_ORDER.indexOf(a.key) - BUCKET_ORDER.indexOf(b.key))

  // As in old Argus: reset on the left, export and print on the right, shown even when nothing is sorted yet.
  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      {canManageUsers && (
        <Button variant="outline" type="button" onClick={handleReset}>
          {t('choice:reset_assignments')}
        </Button>
      )}
      <div className="ml-auto flex flex-wrap gap-2">
        <Button variant="outline" type="button" onClick={handleExport} disabled={isExporting}>
          <span className="mr-2 flex">
            <XlsIcon width="18" height="18" />
          </span>
          {t('common:export')}
        </Button>
        <Button variant="outline" type="button" onClick={handlePrint} disabled={isExporting}>
          {t('choice:printable_version')}
        </Button>
      </div>
    </div>
  )

  if (historyPage == null || groups.length === 0) {
    return (
      <div className="mt-4 w-full">
        {toolbar}
        <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
      </div>
    )
  }

  return (
    <div className="mt-4 w-full space-y-6">
      {toolbar}

      <div className="rounded-card bg-card px-6 py-8">
        {/* One table with a single frozen header, as in old Argus; each bucket is its own tbody. */}
        <Table.Table className="w-full table-auto" scrollable={false}>
          <Table.Thead>
            <HeaderRow expandCodes={expandCodes} onToggleExpandCodes={toggleExpandCodes} />
          </Table.Thead>
          {groups.map((group) => (
            <GroupRows
              key={group.key}
              group={group}
              programTargets={programTargets}
              editingKey={editingKey}
              expandCodes={expandCodes}
              formatScore={formatScore}
              onStartEditing={setEditingKey}
              onCancelEditing={stopEditing}
              onUpdate={handleUpdate}
              onAssign={handleAssign}
              onToggleDiscard={handleToggleDiscard}
            />
          ))}
        </Table.Table>
      </div>

      <div className="flex justify-end gap-6 rounded-card bg-card px-6 py-4 font-semibold text-primaryTextColor">
        <span>
          {t('common:total')} · {t('course:course_credits')}: {historyPage.summary.courseCreditsTotal}
        </span>
        <span>
          {t('common:gain_credits')}: {historyPage.summary.gainedCreditsTotal}
        </span>
      </div>
    </div>
  )
}

interface RowHandlers {
  /** Long curriculum codes wrap at 200px unless the administrator expands them. */
  readonly expandCodes: boolean
  readonly programTargets: ProgramTargets
  readonly formatScore: ScoreFormatter
  readonly onCancelEditing: () => void
  readonly onUpdate: (choice: Api.AdministratorChoiceHistory3Row, values: FormikValues) => Promise<void>
  readonly onAssign: (choice: Api.AdministratorChoiceHistory3Row, programId: number) => Promise<void>
  readonly onToggleDiscard: (choice: Api.AdministratorChoiceHistory3Row) => Promise<void>
}

const HEADER_CELL_CLASS_NAME = 'sticky top-0 z-10 bg-card'

function HeaderRow({
  expandCodes,
  onToggleExpandCodes,
}: {
  readonly expandCodes: boolean
  readonly onToggleExpandCodes: () => void
}): JSX.Element {
  const t = useTranslatable()
  return (
    <Table.Tr>
      <Table.Th className={HEADER_CELL_CLASS_NAME}>{t('course:course_name')}</Table.Th>
      <Table.Th className={HEADER_CELL_CLASS_NAME}>
        <div className="flex flex-col gap-1">
          {t('common:code')}
          <label
            htmlFor="expand-codes"
            className="flex cursor-pointer items-center gap-1 text-xs font-normal normal-case"
          >
            <input id="expand-codes" type="checkbox" checked={expandCodes} onChange={onToggleExpandCodes} />
            {t('choice:expand_codes')}
          </label>
        </div>
      </Table.Th>
      <Table.Th className={HEADER_CELL_CLASS_NAME}>{t('course:general_module')}</Table.Th>
      <Table.Th className={HEADER_CELL_CLASS_NAME}>{t('choice:assignment')}</Table.Th>
      <Table.Th className={HEADER_CELL_CLASS_NAME}>{t('course:course_credits')}</Table.Th>
      <Table.Th className={HEADER_CELL_CLASS_NAME}>{t('common:gain_credits')}</Table.Th>
      <Table.Th className={HEADER_CELL_CLASS_NAME}>{t('common:score')}</Table.Th>
      <Table.Th className={HEADER_CELL_CLASS_NAME}>{t('common:semester')}</Table.Th>
      <Table.Th className={HEADER_CELL_CLASS_NAME} />
    </Table.Tr>
  )
}

interface GroupRowsProps extends RowHandlers {
  readonly group: Api.AdministratorChoiceHistoryGroup
  readonly editingKey: string | null
  readonly onStartEditing: (rowKey: string) => void
}

function GroupRows({ group, editingKey, onStartEditing, ...handlers }: GroupRowsProps): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()

  // Stored names carry stray spaces ("სემინარი  II"), which would otherwise sort "II" before "I".
  const sortedItems = React.useMemo(() => {
    const sortKeyOf = (choice: Api.AdministratorChoiceHistory3Row): string =>
      courseNameOf(choice, locale).replace(/\s+/g, ' ')
    return [...group.items].sort((a, b) => sortKeyOf(a).localeCompare(sortKeyOf(b), locale, { numeric: true }))
  }, [group.items, locale])

  // The legacy heading for the catch-all bucket says what it holds; the API's "Other" does not.
  const title = group.key === 'other' ? t('choice:other_courses') : group.title

  return (
    <Table.Tbody>
      <Table.Tr>
        <Table.Td colSpan={COLUMN_COUNT} className="pt-8 font-semibold text-primaryTextColor">
          {title}
        </Table.Td>
      </Table.Tr>
      {sortedItems.map((choice) => {
        // The same choice can sit in several buckets, so the uid alone is not a unique key.
        const rowKey = `${group.key}-${choice.uid}`
        return (
          <ChoiceRow
            key={rowKey}
            rowKey={rowKey}
            choice={choice}
            isEditing={editingKey === rowKey}
            onStartEditing={onStartEditing}
            {...handlers}
          />
        )
      })}
      <Table.Tr className="font-semibold">
        <Table.Td colSpan={3} />
        <Table.Td className="text-end">{t('common:total')}</Table.Td>
        <Table.Td>{group.courseCreditsTotal}</Table.Td>
        <Table.Td>{group.gainedCreditsTotal}</Table.Td>
        <Table.Td colSpan={3} />
      </Table.Tr>
    </Table.Tbody>
  )
}

const FREE_CREDITS_TARGET: AssignmentTarget = { programId: FREE_CREDITS_PROGRAM_ID, name: '' }
const GENERAL_MODULE_TARGET: AssignmentTarget = { programId: GENERAL_MODULE_PROGRAM_ID, name: '' }

/**
 * Where a row may be moved, following old Argus: major rows go to free credits, or to the minor when the
 * student has one and the code links them to it; "other" rows go to free credits; general-module, minor
 * and free-credit rows go to the major when their code links them to it; and a general-module course
 * that was moved out of the general module can go back to it. Rows that were never passed or are
 * discarded cannot be moved, and a row is never offered the place it is already assigned to. The
 * backend has the final say (422).
 */
function assignmentTargetsOf(
  choice: Api.AdministratorChoiceHistory3Row,
  programTargets: ProgramTargets
): readonly AssignmentTarget[] {
  if (choice.isDiscarded || choice.status !== CHOICE_STATUS_PASSED) return []
  return bucketTargetsOf(choice, programTargets).filter((target) => target.programId !== choice.creditsProgId)
}

function linkedTargetsOf(courseCode: string, target: ProgramTarget | null): readonly AssignmentTarget[] {
  return target != null && isLinkedToProgram(courseCode, target.code) ? [target] : []
}

function bucketTargetsOf(
  choice: Api.AdministratorChoiceHistory3Row,
  { major, minor }: ProgramTargets
): readonly AssignmentTarget[] {
  const majorTargets = linkedTargetsOf(choice.courseCode, major)
  const generalModuleTargets = choice.couGeneral === 1 ? [GENERAL_MODULE_TARGET] : []
  switch (choice.bucket) {
    case 'major':
      return [
        ...(minor != null ? linkedTargetsOf(choice.courseCode, minor) : [FREE_CREDITS_TARGET]),
        ...generalModuleTargets,
      ]
    case 'other':
      return [FREE_CREDITS_TARGET]
    case 'general':
      return majorTargets
    case 'free':
    case 'minor':
      return [...majorTargets, ...generalModuleTargets]
    case 'passive':
      return []
  }
}

/**
 * Curriculum codes list the programs a course serves, separated by "/" and "." — e.g.
 * `INTROGEN/CIVENGGEN.ELECENGGEN/0/0`. A course is linked to a program when one of those parts, with
 * or without the `GEN` suffix, is the program code or the faculty prefix it starts with: `BSNGEN` links
 * to `BSNMNG`, `SOC` to `SOCPSCH`, `LFGEN` to `LFBIO`. `ELECTENGGEN` is a different program from
 * `ELECENG`. Older programs have no code; old Argus offers them every row, so they stay linked.
 */
function isLinkedToProgram(courseCode: string, programCode: string): boolean {
  if (programCode === '') return true
  return courseCode.split(/[./]/).some((part) => {
    const prefix = part.replace(/GEN$/, '')
    return prefix !== '' && prefix !== '0' && programCode.startsWith(prefix)
  })
}

interface ChoiceRowProps extends RowHandlers {
  readonly rowKey: string
  readonly choice: Api.AdministratorChoiceHistory3Row
  readonly isEditing: boolean
  readonly onStartEditing: (rowKey: string) => void
}

function ChoiceRow({
  rowKey,
  choice,
  isEditing,
  programTargets,
  expandCodes,
  formatScore,
  onStartEditing,
  onCancelEditing,
  onUpdate,
  onAssign,
  onToggleDiscard,
}: ChoiceRowProps): JSX.Element {
  const t = useTranslatable()
  const canManageUsers = useAdminAccess().canManage('userStudents')
  const locale = useLocale()

  const initialValues = React.useMemo(
    () => ({ couName: choice.courseName, couCode: choice.courseCode, couGeneral: choice.couGeneral === 1 }),
    [choice.couGeneral, choice.courseCode, choice.courseName]
  )

  const handleSubmit = React.useCallback(
    async (values: FormikValues) => await onUpdate(choice, values),
    [choice, onUpdate]
  )
  const handleStartEditing = React.useCallback(() => onStartEditing(rowKey), [onStartEditing, rowKey])
  const handleToggleDiscard = React.useCallback(() => void onToggleDiscard(choice), [choice, onToggleDiscard])

  const courseName = courseNameOf(choice, locale)
  // Any passed row can be passivated, including zero-credit doctoral thesis and pass/fail rows, which old
  // Argus skipped; failed and in-progress rows cannot.
  const canToggleDiscard = canManageUsers && (choice.isDiscarded || choice.status === CHOICE_STATUS_PASSED)
  const assignmentTargets = assignmentTargetsOf(choice, programTargets)

  // Old Argus tints by credit type: recognized credit red, pre-Argus credit green; passive rows blue.
  const rowClassName = classnames({
    'bg-lightBlue': choice.bucket === 'passive',
    'bg-lightRed': choice.creditType === CREDIT_TYPE_RECOGNIZED,
    'bg-lightGreen': choice.creditType === CREDIT_TYPE_PRE_ARGUS,
    'opacity-60': choice.isDiscarded,
  })

  if (isEditing) {
    return (
      <Table.Tr className={rowClassName}>
        <Table.Td colSpan={COLUMN_COUNT} className="rounded-lg">
          <Form initialValues={initialValues} onSubmit={handleSubmit}>
            <FormError />
            <div className="flex flex-wrap items-center gap-3">
              <Field
                type="text"
                name="couName"
                aria-label={t('course:course_name')}
                className={classnames(CHOICE_INPUT_CLASS_NAME, 'max-w-[420px]')}
              />
              <Field
                type="text"
                name="couCode"
                aria-label={t('common:code')}
                className={classnames(CHOICE_INPUT_CLASS_NAME, 'max-w-[200px]')}
              />
              <label className="flex items-center gap-2 text-sm text-primaryTextColor">
                <Field type="checkbox" name="couGeneral" className="size-4 rounded border-borderGrey" />
                {t('course:general_module')}
              </label>
              <Button variant="outline" type="button" onClick={onCancelEditing}>
                {t('common:cancel')}
              </Button>
              <Button variant="blue" type="submit">
                {t('common:update')}
              </Button>
            </div>
          </Form>
        </Table.Td>
      </Table.Tr>
    )
  }

  return (
    <Table.Tr className={rowClassName}>
      <Table.Td className="rounded-l-lg">{courseName}</Table.Td>
      <Table.Td className={classnames(expandCodes ? 'whitespace-nowrap' : 'max-w-[200px] break-words')}>
        {choice.courseCode}
      </Table.Td>
      <Table.Td>{choice.couGeneral === 1 ? t('common:yes') : t('common:no')}</Table.Td>
      <Table.Td>
        <div className="flex items-center gap-1">
          {canManageUsers &&
            assignmentTargets.map((target) => (
              <AssignmentButton key={target.programId} choice={choice} target={target} onAssign={onAssign} />
            ))}
        </div>
      </Table.Td>
      <Table.Td>{choice.courseCredits}</Table.Td>
      <Table.Td>{choice.credits}</Table.Td>
      <Table.Td>{formatScore(choice)}</Table.Td>
      <Table.Td>{choice.semester.name}</Table.Td>
      <Table.Td className="rounded-r-lg">
        <div className="flex items-center">
          {canManageUsers && (
            <button
              type="button"
              className="flex size-7 items-center justify-center rounded-full p-1 hover:bg-table-icon-hover"
              onClick={handleStartEditing}
              aria-label={t('common:edit')}
              title={t('common:edit')}
            >
              <FontAwesomeIcon icon={ProRegularSvgIcons.faPencil} color="gray" size="lg" />
            </button>
          )}
          {canToggleDiscard && (
            <button
              type="button"
              className="flex size-7 items-center justify-center rounded-full p-1 hover:bg-table-icon-hover"
              onClick={handleToggleDiscard}
              aria-label={choice.isDiscarded ? t('choice:undiscard') : t('choice:discard')}
              title={choice.isDiscarded ? t('choice:undiscard') : t('choice:discard')}
            >
              <FontAwesomeIcon
                icon={choice.isDiscarded ? ProRegularSvgIcons.faRotateLeft : ProRegularSvgIcons.faTrashCan}
                color="gray"
                size="lg"
              />
            </button>
          )}
        </div>
      </Table.Td>
    </Table.Tr>
  )
}

function AssignmentButton({
  choice,
  target,
  onAssign,
}: {
  readonly choice: Api.AdministratorChoiceHistory3Row
  readonly target: AssignmentTarget
  readonly onAssign: (choice: Api.AdministratorChoiceHistory3Row, programId: number) => Promise<void>
}): JSX.Element {
  const t = useTranslatable()
  const [isPending, setIsPending] = React.useState(false)

  const label =
    target.programId === FREE_CREDITS_PROGRAM_ID
      ? t('course:assign_to_free_credits')
      : target.programId === GENERAL_MODULE_PROGRAM_ID
        ? t('course:assign_to_general_module')
        : tt('uncategorized:assign_to', { program: target.name })

  const handleClick = React.useCallback(async () => {
    setIsPending(true)
    try {
      await onAssign(choice, target.programId)
    } finally {
      setIsPending(false)
    }
  }, [choice, onAssign, target.programId])

  return (
    <button
      type="button"
      disabled={isPending}
      className="flex size-7 items-center justify-center rounded-full p-1 hover:bg-table-icon-hover disabled:opacity-50"
      onClick={handleClick}
      aria-label={label}
      title={label}
    >
      <AssignmentUp />
    </button>
  )
}
